import { API_URL } from '../config/api';

/**
 * Las llamadas del panel al módulo de seguimiento de pacientes.
 *
 * Están juntas acá y no sueltas en el componente por una razón concreta: las
 * NOTAS viajan por su propia ruta y no vienen adentro del paciente. Teniendo las
 * llamadas a la vista, esa separación se lee de un vistazo; desparramadas entre
 * `useEffect`s, alguien las junta "para ahorrar un pedido" sin darse cuenta de
 * que está metiendo notas privadas en una respuesta que mañana consume otra
 * cosa.
 */

/**
 * Un pedido autenticado.
 *
 * `currentUser` entra como argumento en vez de leerse de un contexto acá adentro
 * para que este archivo no dependa de React y se pueda probar solo.
 */
async function pedir(currentUser, ruta, opciones = {}) {
  if (!currentUser) throw new Error('No hay sesión.');
  const token = await currentUser.getIdToken();
  const res = await fetch(`${API_URL}${ruta}`, {
    ...opciones,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opciones.headers || {}),
    },
  });

  // El backend contesta `{ message }` en los errores y nunca el detalle interno
  // (ver la red de contención de server.js), así que esto es lo más que se puede
  // mostrar — y alcanza, porque los 400 de este módulo sí explican qué faltó.
  let datos = null;
  try { datos = await res.json(); } catch (_) { /* respuesta sin cuerpo */ }
  if (!res.ok) {
    const error = new Error(datos?.message || `Error ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return datos;
}

export const traerCatalogo = (u) => pedir(u, '/api/pacientes/catalogo');

export const traerPacientes = (u) => pedir(u, '/api/pacientes');

export const guardarPaciente = (u, paciente) =>
  pedir(u, '/api/pacientes', { method: 'POST', body: JSON.stringify(paciente) });

export const borrarPaciente = (u, telefono) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}`, { method: 'DELETE' });

export const traerCheckins = (u, telefono) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/checkins`);

export const guardarCheckin = (u, telefono, checkin) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/checkins`, {
    method: 'POST',
    body: JSON.stringify(checkin),
  });

export const traerNotas = (u, telefono) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/notas`);

export const guardarNota = (u, telefono, texto) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/notas`, {
    method: 'POST',
    body: JSON.stringify({ texto }),
  });

export const borrarNota = (u, telefono, notaId) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/notas/${notaId}`, {
    method: 'DELETE',
  });

// ── Capa 2 ─────────────────────────────────────────────────────────────────

/** "Ya lo contacté". No apaga la alerta para siempre: vuelve al período siguiente. */
export const marcarAlertaVista = (u, telefono) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/alerta-vista`, { method: 'POST' });

export const traerBonos = (u, telefono) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos`);

export const guardarBono = (u, telefono, bono) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos`, {
    method: 'POST', body: JSON.stringify(bono),
  });

export const editarBono = (u, telefono, bonoId, cambios) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos/${bonoId}`, {
    method: 'PUT', body: JSON.stringify(cambios),
  });

export const borrarBono = (u, telefono, bonoId) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos/${bonoId}`, { method: 'DELETE' });

/**
 * Gastar una sesión del bono.
 *
 * El servidor lo resuelve en una transacción, así que dos toques seguidos
 * descuentan una sola vez. Igual la pantalla apaga el botón mientras el pedido
 * viaja: que el servidor esté protegido no es razón para dejar que la interfaz
 * se vea como si hubiera descontado dos.
 */
export const usarSesion = (u, telefono, bonoId, datos = {}) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos/${bonoId}/usos`, {
    method: 'POST', body: JSON.stringify(datos),
  });

export const traerUsos = (u, telefono, bonoId) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos/${bonoId}/usos`);

export const deshacerUso = (u, telefono, bonoId, usoId) =>
  pedir(u, `/api/pacientes/${encodeURIComponent(telefono)}/bonos/${bonoId}/usos/${usoId}`, {
    method: 'DELETE',
  });
